import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  MessageCircle, Save, Loader2, Phone, MessageSquare,
  Palette, Move, Eye, ExternalLink, RefreshCw
} from 'lucide-react';

export default function WhatsAppSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    is_enabled: true,
    phone_number: '+1 787 938 6543',
    default_message: 'Hi! I\'m interested in booking a tour.',
    tooltip_text: '👋 Need help? Chat with us!',
    button_color: '#25D366',
    button_size: 60,
    position_bottom: 24,
    position_right: 24,
    show_on_mobile: true,
    show_on_desktop: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/settings/whatsapp');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Error loading WhatsApp settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/whatsapp', settings);
      toast.success('WhatsApp settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testWhatsApp = () => {
    const formattedPhone = settings.phone_number.replace(/[\s\-\(\)]/g, '');
    const url = `https://wa.me/${formattedPhone}${settings.default_message ? `?text=${encodeURIComponent(settings.default_message)}` : ''}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-settings-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            WhatsApp Settings
          </h2>
          <p className="text-muted-foreground mt-1">
            Configure the floating WhatsApp button that appears on all pages
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={testWhatsApp} className="gap-2">
            <ExternalLink className="h-4 w-4" />
            Test WhatsApp
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Settings
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Contact Settings
          </h3>

          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <Label className="text-base">Enable WhatsApp Button</Label>
              <p className="text-sm text-muted-foreground">Show floating button on all pages</p>
            </div>
            <Switch
              checked={settings.is_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
            />
          </div>

          {/* Phone Number */}
          <div>
            <Label>WhatsApp Business Number</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={settings.phone_number}
                onChange={(e) => setSettings({ ...settings, phone_number: e.target.value })}
                placeholder="+1 787 938 6543"
                className="font-mono"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Include country code (e.g., +1 for US/Puerto Rico)
            </p>
          </div>

          {/* Default Message */}
          <div>
            <Label>Pre-filled Message</Label>
            <Textarea
              value={settings.default_message}
              onChange={(e) => setSettings({ ...settings, default_message: e.target.value })}
              placeholder="Hi! I'm interested in booking a tour."
              rows={3}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This message will be pre-filled when customers click the button
            </p>
          </div>

          {/* Tooltip Text */}
          <div>
            <Label>Tooltip Message</Label>
            <Input
              value={settings.tooltip_text}
              onChange={(e) => setSettings({ ...settings, tooltip_text: e.target.value })}
              placeholder="👋 Need help? Chat with us!"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Shows as a popup near the button to encourage clicks
            </p>
          </div>
        </Card>

        {/* Appearance Settings */}
        <Card className="p-6 space-y-6">
          <h3 className="font-semibold flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Appearance
          </h3>

          {/* Button Color */}
          <div>
            <Label>Button Color</Label>
            <div className="flex gap-3 mt-2">
              <input
                type="color"
                value={settings.button_color}
                onChange={(e) => setSettings({ ...settings, button_color: e.target.value })}
                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
              />
              <div className="flex flex-wrap gap-2">
                {['#25D366', '#128C7E', '#075E54', '#34B7F1', '#FF6B6B', '#4ECDC4'].map(color => (
                  <button
                    key={color}
                    onClick={() => setSettings({ ...settings, button_color: color })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${settings.button_color === color ? 'border-black scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Button Size */}
          <div>
            <Label>Button Size: {settings.button_size}px</Label>
            <input
              type="range"
              min="40"
              max="80"
              value={settings.button_size}
              onChange={(e) => setSettings({ ...settings, button_size: parseInt(e.target.value) })}
              className="w-full mt-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Small (40px)</span>
              <span>Large (80px)</span>
            </div>
          </div>

          {/* Position */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Bottom Offset: {settings.position_bottom}px</Label>
              <input
                type="range"
                min="10"
                max="100"
                value={settings.position_bottom}
                onChange={(e) => setSettings({ ...settings, position_bottom: parseInt(e.target.value) })}
                className="w-full mt-2"
              />
            </div>
            <div>
              <Label>Right Offset: {settings.position_right}px</Label>
              <input
                type="range"
                min="10"
                max="100"
                value={settings.position_right}
                onChange={(e) => setSettings({ ...settings, position_right: parseInt(e.target.value) })}
                className="w-full mt-2"
              />
            </div>
          </div>

          {/* Device Visibility */}
          <div className="space-y-3">
            <Label>Show On</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_on_desktop}
                  onChange={(e) => setSettings({ ...settings, show_on_desktop: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Desktop</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_on_mobile}
                  onChange={(e) => setSettings({ ...settings, show_on_mobile: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Mobile</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <Eye className="h-4 w-4" />
            Preview
          </h3>
          
          <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl h-64 overflow-hidden">
            {/* Mock page content */}
            <div className="p-4 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-20 bg-gray-200 rounded mt-4"></div>
            </div>
            
            {/* WhatsApp Button Preview */}
            {settings.is_enabled && (
              <div 
                className="absolute transition-all duration-300"
                style={{
                  bottom: Math.min(settings.position_bottom, 40),
                  right: Math.min(settings.position_right, 40),
                }}
              >
                {/* Tooltip Preview */}
                {settings.tooltip_text && (
                  <div className="absolute bottom-full right-0 mb-2">
                    <div className="bg-white rounded-lg shadow-lg p-2 text-xs max-w-[150px]">
                      {settings.tooltip_text}
                    </div>
                  </div>
                )}
                
                {/* Button Preview */}
                <div
                  className="rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform"
                  style={{
                    width: Math.min(settings.button_size, 50),
                    height: Math.min(settings.button_size, 50),
                    backgroundColor: settings.button_color,
                  }}
                >
                  <MessageCircle className="text-white" style={{ width: Math.min(settings.button_size, 50) * 0.5 }} />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
